/**
 * @fileoverview Hook for fetching detailed profile statistics
 * 
 * Returns stats like join date, items saved, favorite retailer, top category, etc.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ProfileStats {
  joinedAt: Date;
  itemsSaved: number;
  totalMinutes: number;
  totalSessionsCount: number;
  favoriteRetailer: string | null;
  collectionsCount: number;
  pollsCreated: number;
  tryOnsCount: number;
  followingCount: number;
  followersCount: number;
}

export function useProfileStats(userId?: string) {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  return useQuery({
    queryKey: ['profile-stats', targetUserId],
    queryFn: async (): Promise<ProfileStats | null> => {
      if (!targetUserId) return null;

      // Fetch profile for join date
      const profilePromise = supabase
        .from('profiles')
        .select('created_at')
        .eq('user_id', targetUserId)
        .single();

      // Fetch saved items for count and retailer stats (with JOINs for store)
      const itemsPromise = supabase
        .from('saved_items')
        .select('id, store, page_id, flux_pages:page_id(tld, data)')
        .eq('user_id', targetUserId);

      // Fetch collections count
      const collectionsPromise = supabase
        .from('collections')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', targetUserId);

      // Fetch polls count
      const pollsPromise = supabase
        .from('polls')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', targetUserId);

      // Fetch try-on jobs count
      const tryOnsPromise = supabase
        .from('tryon_jobs')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', targetUserId);

      // Fetch actual time-in-app from heartbeat-based DB function
      const timePromise = supabase
        .rpc('get_user_time_in_app', { target_user_id: targetUserId });

      // Fetch follow counts
      const followingCountPromise = supabase
        .from('follows')
        .select('id', { count: 'exact', head: true })
        .eq('follower_id', targetUserId);

      const followersCountPromise = supabase
        .from('follows')
        .select('id', { count: 'exact', head: true })
        .eq('following_id', targetUserId);

      const [profileRes, itemsRes, collectionsRes, pollsRes, tryOnsRes, timeRes, followingRes, followersRes] = await Promise.all([
        profilePromise,
        itemsPromise,
        collectionsPromise,
        pollsPromise,
        tryOnsPromise,
        timePromise,
        followingCountPromise,
        followersCountPromise,
      ]);

      if (profileRes.error) {
        console.error('Error fetching profile:', profileRes.error);
        return null;
      }

      const items = itemsRes.data || [];
      const itemsSaved = items.length;

      // Extract time stats from DB function
      const timeData = timeRes.data?.[0] || { total_minutes: 0, session_count: 0 };
      const totalMinutes = timeData.total_minutes || 0;
      const totalSessionsCount = timeData.session_count || 0;

      // Calculate favorite retailer (resolve store from flux_pages → saved_items)
      const retailerCounts: Record<string, number> = {};
      items.forEach((item: any) => {
        const fp = item.flux_pages;
        const d = fp?.data;
        const store = (item.page_id && d && typeof d === 'object')
          ? (fp.tld || d.brand || item.store || null)
          : (item.store ?? null);
        if (store) {
          retailerCounts[store] = (retailerCounts[store] || 0) + 1;
        }
      });
      const favoriteRetailer = Object.entries(retailerCounts)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || null;

      return {
        joinedAt: new Date(profileRes.data.created_at),
        itemsSaved,
        totalMinutes,
        totalSessionsCount,
        favoriteRetailer,
        collectionsCount: collectionsRes.count || 0,
        pollsCreated: pollsRes.count || 0,
        tryOnsCount: tryOnsRes.count || 0,
        followingCount: followingRes.count || 0,
        followersCount: followersRes.count || 0,
      };
    },
    enabled: !!targetUserId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
